import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from "react";
import { FRONTIER_MODELS, DEFAULT_MODEL_ID, findModel } from "@/lib/frontierModels";

const ModelContext = createContext(null);

/**
 * ModelProvider — global state for the currently selected frontier model.
 *
 * The header picker updates it; the Wikifi workspace reads it as the default
 * model for outgoing prompts. Persisted in localStorage under
 * `wikifi-model` so the user's choice survives refreshes and tabs.
 */
export function ModelProvider({ children }) {
  const [modelId, setModelIdState] = useState(() => {
    try {
      const saved = localStorage.getItem("wikifi-model");
      return findModel(saved).id;
    } catch {
      return DEFAULT_MODEL_ID;
    }
  });

  useEffect(() => {
    try { localStorage.setItem("wikifi-model", modelId); } catch { /* ignore */ }
  }, [modelId]);

  const setModelId = useCallback((id) => {
    setModelIdState(findModel(id).id);
  }, []);

  const value = useMemo(() => ({
    models: FRONTIER_MODELS,
    modelId,
    model: findModel(modelId),
    setModelId,
  }), [modelId, setModelId]);

  return <ModelContext.Provider value={value}>{children}</ModelContext.Provider>;
}

export function useModel() {
  const ctx = useContext(ModelContext);
  if (!ctx) throw new Error("useModel must be used inside <ModelProvider>");
  return ctx;
}
